<section class="d-flex flex-column gap-2" data-testid="audit-log-page">
  <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
    <div class="d-flex flex-column gap-1">
      <h2 class="av-section-title m-0">{{ 'backoffice.ops.audit.title' | translate }}</h2>
      <p class="av-meta m-0">{{ 'backoffice.ops.audit.subtitle' | translate }}</p>
    </div>

    <button type="button" class="av-btn" (click)="reload()" data-testid="audit-reload">
      <mat-icon aria-hidden="true">refresh</mat-icon>
      <span>{{ 'backoffice.common.reload' | translate }}</span>
    </button>
  </div>

  <p class="av-meta m-0" data-testid="audit-scope-note">{{ 'backoffice.ops.audit.scope_note' | translate }}</p>
</section>

<section class="av-card mt-3 p-4 d-flex flex-column gap-3" data-testid="audit-log-body">
  @switch (facade.state()) {
    @case ('error') {
      <av-empty-state
        icon="error_outline"
        [title]="'backoffice.ops.audit.error_title' | translate"
        [message]="'backoffice.ops.audit.error_message' | translate"
      ></av-empty-state>
    }
    @case ('ready') {
      <div class="filter-row d-flex flex-wrap align-items-end gap-2">
        <label class="d-flex flex-column gap-1" for="audit-action">
          <span class="av-meta">{{ 'backoffice.ops.audit.filters.action' | translate }}</span>
          <select
            id="audit-action"
            class="av-field"
            [value]="facade.filters().action"
            (change)="changeFilter('action', $any($event.target).value)"
            data-testid="audit-action-filter"
          >
            <option value="">{{ 'backoffice.ops.audit.filters.any_action' | translate }}</option>
            @for (action of facade.actions(); track action) {
              <option [value]="action">{{ action }}</option>
            }
          </select>
        </label>

        <label class="d-flex flex-column gap-1" for="audit-entity">
          <span class="av-meta">{{ 'backoffice.ops.audit.filters.entity_type' | translate }}</span>
          <select
            id="audit-entity"
            class="av-field"
            [value]="facade.filters().entity_type"
            (change)="changeFilter('entity_type', $any($event.target).value)"
            data-testid="audit-entity-filter"
          >
            <option value="">{{ 'backoffice.ops.audit.filters.any_entity' | translate }}</option>
            @for (entity of facade.entityTypes(); track entity) {
              <option [value]="entity">{{ entity }}</option>
            }
          </select>
        </label>

        <av-organization-picker
          [organizations]="facade.organizations()"
          [value]="facade.filters().organization_id"
          label="backoffice.ops.audit.filters.organization"
          emptyLabel="backoffice.ops.audit.filters.any_organization"
          (valueChange)="changeFilter('organization_id', $event)"
        ></av-organization-picker>

        <label class="d-flex flex-column gap-1" for="audit-from">
          <span class="av-meta">{{ 'backoffice.ops.audit.filters.from' | translate }}</span>
          <input
            id="audit-from"
            type="date"
            class="av-field"
            [value]="facade.filters().from"
            (change)="changeFilter('from', $any($event.target).value)"
          />
        </label>

        <label class="d-flex flex-column gap-1" for="audit-to">
          <span class="av-meta">{{ 'backoffice.ops.audit.filters.to' | translate }}</span>
          <input
            id="audit-to"
            type="date"
            class="av-field"
            [value]="facade.filters().to"
            (change)="changeFilter('to', $any($event.target).value)"
          />
        </label>

        @if (facade.hasActiveFilters()) {
          <button
            type="button"
            class="av-btn"
            (click)="clearFilters()"
            data-testid="audit-clear-filters"
          >
            {{ 'backoffice.ops.audit.filters.clear' | translate }}
          </button>
        }
      </div>

      @if (isEmpty()) {
        <av-empty-state
          icon="history"
          [title]="
            (facade.hasActiveFilters()
              ? 'backoffice.ops.audit.filtered_empty_title'
              : 'backoffice.ops.audit.empty_title'
            ) | translate
          "
          [message]="
            (facade.hasActiveFilters()
              ? 'backoffice.ops.audit.filtered_empty_message'
              : 'backoffice.ops.audit.empty_message'
            ) | translate
          "
        ></av-empty-state>
      } @else {
        <div class="table-scroll">
          <table class="av-table w-100" data-testid="audit-table">
            <thead>
              <tr>
                <th scope="col">{{ 'backoffice.ops.audit.table.when' | translate }}</th>
                <th scope="col">{{ 'backoffice.ops.audit.table.action' | translate }}</th>
                <th scope="col">{{ 'backoffice.ops.audit.table.entity' | translate }}</th>
                <th scope="col">{{ 'backoffice.ops.audit.table.actor' | translate }}</th>
                <th scope="col">{{ 'backoffice.ops.audit.table.role_at_the_time' | translate }}</th>
                <th scope="col"></th>
              </tr>
            </thead>

            <tbody>
              @for (entry of facade.entries(); track entry.id) {
                <tr>
                  <td class="cell-muted">
                    {{ entry.created_at ? (entry.created_at | date: dateTimeFormat) : unresolved }}
                  </td>

                  <td class="cell-strong av-source">{{ entry.action || unresolved }}</td>

                  <td class="cell-muted">
                    {{ entry.entity_type || unresolved }}
                    <div class="av-source">{{ entry.entity_id || unresolved }}</div>
                  </td>

                  <td
                    class="cell-muted"
                    [matTooltip]="
                      entry.isScheduled ? ('backoffice.ops.audit.system_actor_hint' | translate) : ''
                    "
                    data-testid="audit-actor"
                  >
                    {{ entry.actorLabel | translate }}
                  </td>

                  <td class="cell-muted">{{ entry.roleLabelKey ? (entry.roleLabelKey | translate) : unresolved }}</td>

                  <td>
                    <button
                      type="button"
                      class="av-btn"
                      (click)="toggleEntry(entry.id)"
                      data-testid="audit-toggle-context"
                    >
                      {{ 'backoffice.ops.audit.context' | translate }}
                    </button>
                  </td>
                </tr>

                @if (facade.selectedEntryId() === entry.id) {
                  <tr>
                    <td colspan="6">
                      <pre class="av-source context-json" data-testid="audit-context">{{
                        entry.contextJson
                      }}</pre>
                    </td>
                  </tr>
                }
              }
            </tbody>
          </table>
        </div>

        <av-paginator
          [page]="facade.page()"
          [pageSize]="facade.pageSize()"
          [total]="facade.total()"
          (pageChange)="goToPage($event)"
          (pageSizeChange)="changePageSize($event)"
        ></av-paginator>
      }
    }
    @default {
      <av-empty-state
        icon="hourglass_empty"
        [title]="'backoffice.ops.audit.loading_title' | translate"
        [message]="'backoffice.ops.audit.loading_message' | translate"
      ></av-empty-state>
    }
  }
</section>
